import { Observable, Subject } from 'rxjs';
import { filter, first, takeUntil } from 'rxjs/operators';
import { Injectable, ComponentRef, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MmAlertService } from '@metromarkets/components';

import { TranslationKeysUtil } from '~shared/utils/translation-keys.util';

import { IPageInfo } from '../models/account.model';
import { ApplicationEventService } from '~shared/modules/application-event/services';
import { OrderHistorySideMenuApplicationEvent } from '~shared/modules/application-event/models';

@Injectable()
export class AccountService implements OnDestroy {
  private unsubscribe$ = new Subject<void>();
  public currentPage: IPageInfo;
  public parentPage: IPageInfo;
  public pagePathWithFirstQueryParameter: string;
  public get routeChange$(): Observable<void> {
    return this._routeChange$.asObservable();
  }

  private _routeChange$ = new Subject<void>();
  private alertRef: ComponentRef<any>;

  private static getPageInfo(path): IPageInfo {
    return {
      path,
      title: TranslationKeysUtil.getPageTitle(path),
    };
  }

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private alertService: MmAlertService,
    private applicationEventService: ApplicationEventService,
  ) {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.unsubscribe$)
      ).subscribe(() => this.handleRouteChange());
  }

  public showGlobalAlert(message = '', type = 'success'): void {
    this.translateService.get(message)
      .pipe(first())
      .subscribe((translatedMessage: string) => {
        this.createAlert(translatedMessage, type);
      });
  }

  public showContextAlert(message = '', type = 'success'): void {
    this.closeContextAlert();

    this.translateService.get(message)
      .pipe(first())
      .subscribe((translatedMessage: string) => {
        this.alertRef = this.createAlert(translatedMessage, type, '.fn-account__alert-container');
      });
  }

  public closeContextAlert(): void {
    if (this.alertRef) {
      this.alertRef.destroy();
      this.alertRef = null;
    }
  }

  private handleRouteChange(): void {
    this.closeContextAlert();
    this.updatePageInfo();
    this._routeChange$.next();
  }

  private updatePageInfo(): void {
    const pagePathWithQueryParams = this.router.url;
    const pagePath = pagePathWithQueryParams.split('?')[0];
    this.pagePathWithFirstQueryParameter =  pagePathWithQueryParams.split('&')[0];
    const parentPagePath = pagePath.replace(/\/(?:[^\/])*$/, '');
    this.currentPage = AccountService.getPageInfo(pagePath);
    this.parentPage = AccountService.getPageInfo(parentPagePath);
  }

  private createAlert(message: string, type: string, target?: string) {
    return this.alertService.addAlert({
      type,
      target,
      title: message,
      message: '',
      mode: 'banner',
      size: 'large',
      duration: 4000,
      contentAlign: 'center',
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public sendGAEvents(label: string): void {
    this.applicationEventService.publish(new OrderHistorySideMenuApplicationEvent(label));
  }
}
